import React, { useCallback, useState } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { useLazyCommand, useDomainEvent } from '@soap/modules';

interface RegenerateLessonDialogProps {
    unitId: string;
    lessonId: string;
    lessonTitle: string;
    onClose: (wasConfirmed: boolean) => void;
}

const viewStates = {
    ready: 0,
    submitting: 1
};

export function RegenerateLessonDialog({
    unitId,
    lessonId,
    lessonTitle,
    onClose
}: RegenerateLessonDialogProps) {
    const [viewState, setViewState] = useState(viewStates.ready);
    const [sendRegenerateLessonCommand] = useLazyCommand('C334v1_RegenerateLesson');
    const triggerCloseContextMenu = useDomainEvent({
        eventName: 'close-context-menu'
    });

    const handleClose = useCallback(
        (wasConfirmed: boolean = false) => {
            setViewState(viewStates.ready);
            onClose(wasConfirmed);
            triggerCloseContextMenu();
        },
        [onClose, triggerCloseContextMenu]
    );

    const handleRegenerate = async () => {
        setViewState(viewStates.submitting);

        try {
            await sendRegenerateLessonCommand({
                C334_UnitId: unitId,
                C334_LessonId: lessonId
            });
            handleClose(true);
        } catch (error) {
            console.error('Error regenerating lesson:', error);
            setViewState(viewStates.ready);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Regenerate Lesson</h2>
                        <button
                            type="button"
                            onClick={() => handleClose(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                            <p className="text-sm text-orange-800">This action cannot be undone.</p>
                            <p className="text-sm text-orange-800 mt-2">
                                Please confirm you want to regenerate <strong>{lessonTitle}</strong>?
                            </p>
                            <p className="text-sm text-orange-800 mt-2">
                                This will replace this lesson with a new AI-generated one covering the same topics.
                                Other lessons in the unit stay unchanged.
                            </p>
                        </div>

                        <div className="bg-action-blue/10 p-4 rounded-lg border border-action-blue/30">
                            <h4 className="font-medium text-action-blue-dark mb-2 flex items-center">
                                <RefreshCw size={16} className="mr-2" />
                                What happens when you regenerate:
                            </h4>
                            <ul className="ml-6 space-y-1 list-disc">
                                <li className="text-sm text-action-blue-dark">
                                    This lesson&apos;s title, steps, and supporting materials are replaced
                                </li>
                                <li className="text-sm text-action-blue-dark">
                                    The topics this lesson covers stay the same
                                </li>
                                <li className="text-sm text-action-blue-dark">
                                    Video selections for this lesson will need to be reviewed again
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-6">
                        <button
                            type="button"
                            onClick={() => handleClose(false)}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleRegenerate()}
                            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            disabled={viewState === viewStates.submitting}
                        >
                            <RefreshCw size={16} className={`mr-2 ${viewState === viewStates.submitting ? 'animate-spin' : ''}`} />
                            {viewState === viewStates.submitting ? 'Regenerating...' : 'Regenerate Lesson'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
